/**
 * Shared constants for measurement and curve detection algorithms.
 * These constants are used across SurfaceMeasureComputation.js
 * to ensure consistent geometric analysis thresholds.
 *
 * Constants imported from EdgePrimitiveUtils.cpp
 * Reference: https://github.com/plangrid/elephant/blob/be527dc954b98f766e64585eb42de62e4ae6d36d/ElephantVertexGlm/3D/Measurement/EdgePrimitiveUtils.cpp#L20
 */

// ============================================================================
// Geometric Detection Thresholds
// ============================================================================

/** Minimum number of edges required to detect a circular arc */
export const MIN_ARC_EDGES = 3;

/** Angular precision threshold (radians) - approximately 0.057 degrees. Used to detect curved surfaces. */
export const ANGLE_PRECISION = 0.001;

/** Minimum number of edges required to detect a complete circle */
export const MIN_CIRCLE_EDGES = 7;

/** General epsilon for floating-point comparisons */
export const EPSILON = 0.001;

/** Tolerance as percentage of reference value for vertex-on-curve precision (1%) */
export const VERTEX_PRECISION_PERCENT = 0.01;

/** Tolerance as fraction of edge length for edge length comparisons (20%) */
export const EDGE_LENGTH_TOLERANCE = 0.2;

/** Minimum interior angle (radians) for curve continuity detection */
export const MIN_CURVE_ANGLE = Math.PI * 0.75;

/** Maximum interior angle (radians) for curve continuity detection */
export const MAX_CURVE_ANGLE = Math.PI;

/** Epsilon for welding vertices */
export const WELDING_EPSILON = 1e-5;

/** Minimum scale component magnitude below which a matrix scale axis is treated as degenerate */
export const MIN_SCALE_COMPONENT = 1e-6;

/** Crease angle threshold (radians) - 30 degrees. Used to detect curved surfaces. */
export const CREASE_ANGLE_RADIANS = Math.PI / 6;

/** Maximum index buffer length before surface tool bails out entirely. Biggest index buffer tested and worked correctly in the viewer. */
export const MAX_SURFACE_INDEX_BUFFER = 65536;

/** Maximum number of shape dimensions rendered for a surface */
export const MAX_SURFACE_DIMENSIONS = 50;

// ============================================================================
// Shape Type Identifiers
// ============================================================================

/**
 * Enumeration of supported shape types for surface measurement.
 * Used to consistently identify detected geometric primitives.
 */
export const SHAPE_TYPES = {
  CIRCLE: 'circle',
  ELLIPSE: 'ellipse',
  RECTANGLE: 'rectangle',
  SIMPLE_CURVE: 'simple_curve',
  CIRCULAR_ARC: 'circular_arc',
  POLYGON: 'polygon',
  HEIGHT: 'height',
  LINE: 'line'
};